// Extension-qualified so this module is importable by plain Node (see tests).
import { runGraphQL, runNrql } from "../nr1/nerdgraph.js";

/**
 * Reads a New Relic entity — including, when it is a dashboard, the full widget
 * definition — so the standalone build can render the same view the platform
 * shows in its stacked entity panel.
 *
 * Rendering the real definition rather than a hand-built copy means edits made
 * to the dashboard in New Relic show up here with no code change.
 */

const ENTITY_DETAIL = `
  query EntityDetail($guid: EntityGuid!) {
    actor {
      entity(guid: $guid) {
        guid
        name
        entityType
        domain
        type
        accountId
        reporting
        alertSeverity
        tags { key values }

        ... on AlertableEntity {
          recentAlertViolations(count: 8) {
            violationId label level openedAt closedAt violationUrl
          }
        }

        relatedEntities(limit: 25) {
          results {
            type
            target { entity { guid name entityType domain alertSeverity } }
          }
        }

        ... on DashboardEntity {
          description
          variables {
            name title type replacementStrategy
            defaultValues { value { string } }
          }
          pages {
            name
            widgets {
              id
              title
              layout { column row width height }
              visualization { id }
              rawConfiguration
            }
          }
        }
      }
    }
  }
`;

export async function fetchEntityDetail(guid, { signal } = {}) {
  const data = await runGraphQL({ query: ENTITY_DETAIL, variables: { guid }, signal });
  return data?.actor?.entity ?? null;
}

/**
 * Interpolates `{{name}}` placeholders the way New Relic does.
 *
 * The variable's `replacementStrategy` decides whether the value is quoted.
 * This matters: store numbers carry leading zeros, so a STRING variable must
 * become `store.number = '00156'`. Substituting raw yields
 * `store.number = 00156`, which NRQL reads as the number 156 and silently
 * matches nothing — every widget renders empty with no error.
 */
export function applyVariables(query, values, strategies) {
  if (!query) return query;
  return query.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (match, name) => {
    const value = values?.[name];
    if (value === undefined || value === null) return match;

    const strategy = String(strategies?.[name] || "STRING").toUpperCase();
    if (strategy === "NUMBER" || strategy === "IDENTIFIER") return String(value);

    // STRING (New Relic's default) — quote it, escaping any embedded quote.
    return `'${String(value).replace(/'/g, "\\'")}'`;
  });
}

/** `{ variableName: replacementStrategy }` for a dashboard entity. */
export function variableStrategies(entity) {
  const out = {};
  for (const variable of entity?.variables || []) {
    out[variable.name] = variable.replacementStrategy || "STRING";
  }
  return out;
}

/** Default values declared on the dashboard, used when the caller supplies none. */
export function defaultVariableValues(entity) {
  const out = {};
  for (const variable of entity?.variables || []) {
    const fallback = variable.defaultValues?.[0]?.value?.string;
    if (fallback !== undefined) out[variable.name] = fallback;
  }
  return out;
}

export const isDashboard = (entity) =>
  entity?.entityType === "DASHBOARD_ENTITY" || Array.isArray(entity?.pages);

/**
 * Runs one widget's NRQL. Returns both the SDK-shaped chart data (for charts)
 * and the raw rows (for tables and billboards), since widgets need each.
 */
export async function runWidgetQuery(widget, { variables, strategies, fallbackAccountId, signal }) {
  const config = widget?.rawConfiguration || {};
  const nrql = config.nrqlQueries?.[0];
  if (!nrql?.query) return { rows: [], data: [], empty: true };

  const accountId = nrql.accountIds?.[0] ?? fallbackAccountId;
  const query = applyVariables(nrql.query, variables, strategies);

  const result = await runNrql({ accountId, query, signal });
  return {
    rows: expandFacets(result.rawResults, result.metadata),
    data: result.data,
    query,
    empty: !result.rawResults.length,
  };
}

/**
 * NerdGraph returns a faceted row as a positional `facet` array, with the
 * column names in `metadata.facets`. Expanding them gives the named columns
 * New Relic shows (State | FOE ID | Date) instead of a raw array.
 */
export function expandFacets(rows, metadata) {
  const names = metadata?.facets;
  if (!Array.isArray(rows) || !Array.isArray(names) || names.length === 0) {
    return rows || [];
  }
  return rows.map((row) => {
    if (!("facet" in row)) return row;
    const { facet, ...rest } = row;
    const values = Array.isArray(facet) ? facet : [facet];
    const named = {};
    names.forEach((name, i) => {
      named[name] = values[i];
    });
    return { ...named, ...rest };
  });
}

export const widgetKind = (widget) =>
  String(widget?.visualization?.id || "").replace(/^viz\./, "");

/* ------------------------------------------------- dashboard resolution */

const DASHBOARD_SEARCH = `
  query FindDashboards($query: String!) {
    actor {
      entitySearch(query: $query) {
        results { entities { guid name accountId } }
      }
    }
  }
`;

const normalise = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const commonPrefix = (a, b) => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
};

/**
 * How well a dashboard name matches an entity type.
 *
 * Markets name these differently, and a shared prefix alone only catches one of
 * the two conventions:
 *
 *   CH  "Digital Order Entity"          DIGITALORDER|ENTITY   prefix 12
 *   PT  "Store Entity Digital Ordering" STOREENTITY|DIGITALORDERING  prefix 0
 *
 * PT puts the service last, so every PT alert scored 0, resolved to no
 * dashboard, and fell back to the EXT entity — which New Relic does not hold
 * for that market, so the panel showed "no entity with this ID" instead of the
 * dashboard that exists.
 *
 * Containing the whole type is at least as strong a signal as sharing a prefix
 * with it, so take whichever is better. The prefix arm still carries the CH
 * spelling, where the name is a truncation rather than a superset.
 */
const nameScore = (wanted, name) =>
  name.includes(wanted) ? Math.max(wanted.length, commonPrefix(wanted, name)) : commonPrefix(wanted, name);

// Resolved lookups are stable for a session; the search is not free.
const dashboardCache = new Map();

/**
 * Finds the dashboard New Relic would show for a synthesised service entity.
 *
 * The health API hands us an EXT entity guid (`3592556|EXT|DIGITALORDERING|00031`)
 * and New Relic maps that entity *type* to a dashboard through its entity
 * synthesis definitions — a mapping NerdGraph does not expose, and which is not
 * present on the entity's relationships or tags.
 *
 * So it is resolved by name within the entity's own account, which keeps it
 * working across markets without a per-market config. `entityDashboards` in
 * config.json overrides this for anything the naming does not cover.
 */
export async function resolveServiceDashboard({ accountId, type, overrides, signal }) {
  if (!accountId || !type) return null;

  const byAccount = overrides?.[String(accountId)];
  const override =
    (byAccount && (byAccount[type] || byAccount[type?.toUpperCase()])) ||
    (typeof overrides?.[type] === "string" ? overrides[type] : null);
  if (override) return override;

  const cacheKey = `${accountId}:${type}`;
  if (dashboardCache.has(cacheKey)) return dashboardCache.get(cacheKey);

  // The proxy requires the search be scoped to a permitted account.
  const query = `accountId = ${accountId} AND type = 'DASHBOARD' AND name LIKE '%Entity%'`;
  const data = await runGraphQL({ query: DASHBOARD_SEARCH, variables: { query }, signal });

  const candidates = (data?.actor?.entitySearch?.results?.entities || []).filter(
    // Sub-pages surface as "Parent / Page"; only the parent is openable.
    (e) => !e.name.includes(" / ")
  );

  const wanted = normalise(type);
  let best = null;
  let bestScore = 0;
  for (const candidate of candidates) {
    const score = nameScore(wanted, normalise(candidate.name));
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  // Require a substantial match so an unrelated dashboard is never shown.
  // "SESAME" vs "SESAMEENTITY" scores 6; "MYSTORE" vs "STOREENTITY" still 0 —
  // it shares no prefix and is not contained, which is what keeps MyStore off
  // the generic Store Entity dashboard.
  const resolved = bestScore >= 5 ? best.guid : null;
  dashboardCache.set(cacheKey, resolved);
  return resolved;
}
